/* eslint-disable no-restricted-syntax, prettier/prettier */
import React from 'react';

const IS_HTML = (s) => /<[a-z][\s\S]*>/i.test(s);

export const markdownToHTML = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  let html = '';
  // null | 'ul' | 'ol' — tracks which list wrapper (if any) is currently
  // open, so a bullet line and a numbered line never share one <ul>/<ol>
  // and the right closing tag always matches what was opened.
  let listType = null;
  const closeList = () => {
    if (listType) html += `</${listType}>`;
    listType = null;
  };
  // Real bold markers are always a clean "**pair**", but hand-typed or
  // repeatedly-bold-toggled source can leave this looking like
  // "******text 2**********" (runs of 3+ stars from re-wrapping an
  // already-bold selection) or an orphaned single "**" with no partner
  // (e.g. a bold span whose closing marker landed on its own line after a
  // stray Enter) — collapsing runs of 2+ stars down to a clean pair first
  // lets the real ones match, and stripping whatever's left over (orphans
  // with no partner) keeps stray asterisks from ever showing up as literal
  // text in the rendered note. Bold is resolved first so a clean "**" pair
  // never gets mistaken for two single-star italic markers; underline has
  // no real markdown syntax, so this app-internal format uses a single
  // "_pair_" for it (the inverse of htmlToMarkdown's own `<u>` encoding).
  const inline = (s) =>
    s
      .replace(/\*{2,}/g, '**')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*\*/g, '')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<u>$1</u>');
  for (const line of lines) {
    const numbered = line.match(/^\d+\.\s(.*)/);
    if (/^### /.test(line)) {
      closeList();
      html += `<h3>${inline(line.slice(4))}</h3>`;
    } else if (/^## /.test(line)) {
      closeList();
      html += `<h2>${inline(line.slice(3))}</h2>`;
    } else if (/^# /.test(line)) {
      closeList();
      html += `<h1>${inline(line.slice(2))}</h1>`;
    } else if (/^- /.test(line)) {
      if (listType !== 'ul') {
        closeList();
        html += '<ul>';
        listType = 'ul';
      }
      html += `<li>${inline(line.slice(2))}</li>`;
    } else if (numbered) {
      if (listType !== 'ol') {
        closeList();
        html += '<ol>';
        listType = 'ol';
      }
      html += `<li>${inline(numbered[1])}</li>`;
    } else if (line.trim() === '') {
      closeList();
    } else {
      closeList();
      html += `<p>${inline(line)}</p>`;
    }
  }
  closeList();
  return html;
};

const BLOCK_TAGS = new Set(['h1', 'h2', 'h3', 'ul', 'ol']);

// True when none of this node's element children are themselves a block
// (heading/list) — i.e. it's safe to flatten straight to inline text. A
// plain typed line is always like this; a <div> that happens to *wrap* a
// list/heading (see note below) is not.
const isInlineOnly = (node) =>
  Array.from(node.childNodes).every(
    (child) =>
      child.nodeType !== Node.ELEMENT_NODE ||
      !BLOCK_TAGS.has(child.tagName.toLowerCase())
  );

// Inverse of markdownToHTML — walks the parsed DOM (h1/h2/h3, ul/ol>li, p,
// strong/b/em/i/u) back into the same line-based markdown syntax, so a
// field can round-trip through an HTML-emitting editor (MarkdownEditor)
// while its stored/API value stays markdown source. Only inverts the
// subset markdownToHTML itself produces; anything else (tables, links)
// falls back to its plain text content.
export const htmlToMarkdown = (html) => {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html;

  const inline = (node) => {
    let text = '';
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();
        if (tag === 'strong' || tag === 'b') {
          text += `**${inline(child)}**`;
        } else if (tag === 'em' || tag === 'i') {
          text += `*${inline(child)}*`;
        } else if (tag === 'u') {
          text += `_${inline(child)}_`;
        } else if (tag === 'br') {
          text += '\n';
        } else {
          text += inline(child);
        }
      }
    });
    return text;
  };

  // A contentEditable div's own Enter-created wrapper (e.g. the <div> a
  // browser puts around a line before/after a list) isn't itself a
  // heading/list/paragraph — it's a transparent container. Walking only
  // `container`'s direct children (as this used to) treated such a
  // wrapper as one opaque block and flattened everything inside it
  // (headings, and every <li> in a list) into one run-on inline string
  // with no markers or separators at all. Recursing into any wrapper that
  // itself contains a block-level child fixes that without changing how a
  // genuinely plain typed line (no nested block) is handled.
  const blocks = [];
  const walk = (parent) => {
    parent.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) blocks.push(text);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName.toLowerCase();
      if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
        const prefix = { h1: '#', h2: '##', h3: '###' }[tag];
        const text = inline(node).trim();
        if (text) blocks.push(`${prefix} ${text}`);
      } else if (tag === 'ul' || tag === 'ol') {
        const isOrdered = tag === 'ol';
        const items = Array.from(node.children)
          .filter((child) => child.tagName.toLowerCase() === 'li')
          .map((li) => inline(li).trim())
          .filter(Boolean)
          .map((text, i) => (isOrdered ? `${i + 1}. ${text}` : `- ${text}`));
        if (items.length) blocks.push(items.join('\n'));
      } else if (!isInlineOnly(node)) {
        walk(node);
      } else {
        const text = inline(node).trim();
        if (text) blocks.push(text);
      }
    });
  };
  walk(container);

  return blocks.join('\n\n');
};

const PROSE_CLS = [
  'text-[13.5px] leading-[1.75]',
  '[&_h1]:text-[18px] [&_h1]:font-bold [&_h1]:text-foreground-strong [&_h1]:mt-5 [&_h1]:mb-2 [&_h1:first-child]:mt-0',
  '[&_h2]:text-[14px] [&_h2]:font-[650] [&_h2]:text-foreground-strong [&_h2]:mt-5 [&_h2]:mb-1.5',
  '[&_h3]:text-[13.5px] [&_h3]:font-[700] [&_h3]:text-foreground-strong [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:tracking-[-0.01em]',
  '[&_p]:my-1.5',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-1 [&_ul]:mb-3',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mt-1 [&_ol]:mb-3',
  '[&_li]:mb-0.5',
  '[&_strong]:font-[650] [&_strong]:text-foreground-strong',
  '[&_em]:italic',
].join(' ');

const MarkdownViewer = ({ content, className = '' }) => {
  if (!content) return null;
  const html = IS_HTML(content) ? content : markdownToHTML(content);
  return (
    <div
      className={`${PROSE_CLS} ${className}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default MarkdownViewer;
